import { eventBus } from '../core/EventBus';
import { lerp, lerpColor } from '../core/Tween';

export class Environment {
  lightLevel: number = 80;
  temperature: number = 22;
  timeOfDay: number = 12;

  displayLightLevel: number = 80;
  displayTimeOfDay: number = 12;
  private displaySkyR: number = 248;
  private displaySkyG: number = 237;
  private displaySkyB: number = 222;
  private displayHorizonR: number = 255;
  private displayHorizonG: number = 240;
  private displayHorizonB: number = 220;

  private lightCycle: boolean = true;

  private dayTime: number = 12 * 60; // game-minutes, but advances at 1/4 speed for slower day/night
  private readonly DAY_CYCLE_SLOWDOWN = 0.25;

  weather: 'sunny' | 'cloudy' = 'sunny';

  constructor() {
    eventBus.on('game:update', this.update.bind(this));
  }

  private update(deltaTime: number, _gameTime: number): void {
    // Day/night cycle advances at 1/4 the normal game speed
    this.dayTime += deltaTime * this.DAY_CYCLE_SLOWDOWN;
    this.timeOfDay = (this.dayTime / 60) % 24;

    if (this.lightCycle) {
      const hour = this.timeOfDay;
      if (hour >= 6 && hour <= 18) {
        const midDay = 12;
        const dist = Math.abs(hour - midDay) / 6;
        this.lightLevel = Math.round(80 * (1 - dist * dist));
      } else {
        this.lightLevel = 0;
      }
    }

    const targetTemp = 15 + (this.lightLevel / 100) * 15;
    this.temperature += (targetTemp - this.temperature) * 0.001 * deltaTime;
  }

  updateDisplay(): void {
    this.displayTimeOfDay = lerp(this.displayTimeOfDay, this.timeOfDay, 0.08);
    this.displayLightLevel = lerp(this.displayLightLevel, this.lightLevel, 0.06);

    const [tr, tg, tb] = this.getTargetSkyRGB();
    this.displaySkyR = lerp(this.displaySkyR, tr, 0.07);
    this.displaySkyG = lerp(this.displaySkyG, tg, 0.07);
    this.displaySkyB = lerp(this.displaySkyB, tb, 0.07);

    const [hr, hg, hb] = this.getTargetHorizonRGB();
    this.displayHorizonR = lerp(this.displayHorizonR, hr, 0.07);
    this.displayHorizonG = lerp(this.displayHorizonG, hg, 0.07);
    this.displayHorizonB = lerp(this.displayHorizonB, hb, 0.07);
  }

  getDisplaySkyColor(): string {
    return `rgb(${Math.round(this.displaySkyR)}, ${Math.round(this.displaySkyG)}, ${Math.round(this.displaySkyB)})`;
  }

  getDisplayHorizonColor(): string {
    return `rgb(${Math.round(this.displayHorizonR)}, ${Math.round(this.displayHorizonG)}, ${Math.round(this.displayHorizonB)})`;
  }

  getDisplayHorizonRGB(): [number, number, number] {
    return [Math.round(this.displayHorizonR), Math.round(this.displayHorizonG), Math.round(this.displayHorizonB)];
  }

  private getTargetSkyRGB(): [number, number, number] {
    const hour = this.timeOfDay;
    const night = [40, 45, 60] as [number, number, number];
    const dawn = [255, 200, 180] as [number, number, number];
    const dusk = [255, 180, 160] as [number, number, number];

    if (hour < 4) {
      return night;
    } else if (hour < 5) {
      return lerpColor(night, dawn, (hour - 4)) as [number, number, number];
    } else if (hour < 6) {
      const t = hour - 5;
      const day = this.getDaySkyRGB(hour);
      return lerpColor(dawn, day, t) as [number, number, number];
    } else if (hour <= 18) {
      return this.getDaySkyRGB(hour);
    } else if (hour < 19.5) {
      const t = (hour - 18) / 1.5;
      const day = this.getDaySkyRGB(Math.min(hour, 18));
      return lerpColor(day, dusk, t) as [number, number, number];
    } else if (hour < 21) {
      const t = (hour - 19.5) / 1.5;
      return lerpColor(dusk, night, t) as [number, number, number];
    } else {
      return night;
    }
  }

  private getTargetHorizonRGB(): [number, number, number] {
    const hour = this.timeOfDay;
    const day = [255, 240, 220] as [number, number, number];
    const dusk = [255, 200, 160] as [number, number, number];
    const night = [120, 140, 180] as [number, number, number];
    const dawn = [255, 220, 200] as [number, number, number];

    if (hour < 4.5) {
      return night;
    } else if (hour < 5.5) {
      return lerpColor(night, dawn, (hour - 4.5)) as [number, number, number];
    } else if (hour < 18) {
      return day;
    } else if (hour < 19.5) {
      return lerpColor(day, dusk, (hour - 18) / 1.5) as [number, number, number];
    } else if (hour < 21.5) {
      return lerpColor(dusk, night, (hour - 19.5) / 2) as [number, number, number];
    } else {
      return night;
    }
  }

  setLightLevel(level: number): void {
    this.lightLevel = Math.max(0, Math.min(100, level));
    this.lightCycle = false;
  }

  enableLightCycle(): void {
    this.lightCycle = true;
  }

  isLightCycleActive(): boolean {
    return this.lightCycle;
  }

  setLightCycle(active: boolean): void {
    this.lightCycle = active;
  }

  getLightColor(): string {
    const intensity = this.lightLevel / 100;
    const hour = this.timeOfDay;
    let warmth = 0.8;
    if (hour < 8 || hour > 16) {
      warmth = 0.6;
    }
    if (hour < 6 || hour > 20) {
      warmth = 0.4;
    }

    const r = Math.round(255 * intensity * warmth);
    const g = Math.round(255 * intensity * (warmth + 0.1));
    const b = Math.round(255 * intensity * (warmth + 0.2));

    return `rgba(${r}, ${g}, ${b}, ${0.3 + intensity * 0.4})`;
  }

  getSkyColor(): string {
    const [r, g, b] = this.getTargetSkyRGB();
    return `rgb(${r}, ${g}, ${b})`;
  }

  private getDaySkyRGB(hour: number): [number, number, number] {
    const brightness = 1 - Math.abs(hour - 12) / 12;
    if (this.weather === 'cloudy') {
      return [
        Math.round(200 + brightness * 20),
        Math.round(205 + brightness * 25),
        Math.round(210 + brightness * 30),
      ];
    }
    return [
      Math.round(168 + brightness * 40),
      Math.round(200 + brightness * 35),
      Math.round(224 + brightness * 31),
    ];
  }

  getSunPosition(): { x: number; y: number; visible: boolean } {
    const hour = this.displayTimeOfDay;
    if (hour < 5 || hour > 19) return { x: 0.5, y: 1.2, visible: false };
    const t = (hour - 5) / 14;
    const angle = Math.PI + t * Math.PI;
    return {
      x: 0.5 + Math.cos(angle) * 0.4,
      y: 0.88 + Math.sin(angle) * 0.75,
      visible: true
    };
  }

  getMoonPosition(): { x: number; y: number; visible: boolean } {
    const hour = this.displayTimeOfDay;
    if (hour > 6 && hour < 18) return { x: 0.5, y: 1.2, visible: false };
    const effectiveHour = hour >= 18 ? hour - 18 : hour + 6;
    const t = effectiveHour / 12;
    const angle = Math.PI + t * Math.PI;
    return {
      x: 0.5 + Math.cos(angle) * 0.35,
      y: 0.82 + Math.sin(angle) * 0.65,
      visible: true
    };
  }

  getSunOpacity(): number {
    const hour = this.displayTimeOfDay;
    if (hour < 5 || hour > 19) return 0;
    if (hour < 7) return (hour - 5) / 2;
    if (hour > 17) return (19 - hour) / 2;
    return 1;
  }

  getMoonOpacity(): number {
    const hour = this.displayTimeOfDay;
    if (hour > 6 && hour < 18) return 0;
    if (hour >= 18) return Math.min(1, (hour - 18) / 2);
    if (hour < 6) return Math.min(1, (6 - hour) / 2);
    return 1;
  }
}
